<template>
    <VJoyWrapper
        :class="{recommendation: true, 'recommendation--sample': isSample}"
        no-margin
        direction="row"
        wrap="nowrap"
        align="flex-start"
        column-gap="0"
    >
        <VJoyWrapper no-margin direction="row" wrap="nowrap" align="flex-start" column-gap="4">
            <VJoyAvatar
                class="recommendation__header-avatar"
                :full-name="`${recommendation.authorFirstName} ${recommendation.authorLastName}`"
                :photo-url="recommendation.authorImgUrl || undefined"
            />
            <VJoyWrapper no-margin direction="column" align="stretch" column-gap="0" row-gap="0">
                <VJoyWrapper no-margin direction="row" column-gap="6" row-gap="0">
                    <VJoyWrapper
                        v-if="recommendation.authorFirstName && recommendation.authorProfileUrl"
                        class="recommendation__link-name"
                        no-margin
                        column-gap="1"
                        row-gap="0"
                    >
                        <VJoyIcon name="user-lighter" size="xsmall" />
                        <VJoyLink :href="authorProfileUrl">
                            <span>{{ recommendation.authorFirstName }} {{ recommendation.authorLastName }}</span>
                        </VJoyLink>
                    </VJoyWrapper>
                    <VJoyWrapper v-else-if="recommendation.authorFirstName" no-margin column-gap="1" row-gap="0">
                        <VJoyIcon name="user-lighter" size="xsmall" />
                        <span>{{ recommendation.authorFirstName }} {{ recommendation.authorLastName }}</span>
                    </VJoyWrapper>
                    <VJoyWrapper v-if="recommendation.authorCompanyName" no-margin wrap="nowrap" column-gap="1" row-gap="0">
                        <VJoyIcon name="company-placeholder" size="xsmall" />
                        <span class="recommendation__author-company">{{ recommendation.authorCompanyName }}</span>
                    </VJoyWrapper>
                </VJoyWrapper>
                <div class="recommendation__author-job">{{ recommendation.authorJobTitle }}</div>
                <div class="recommendation__date">{{ dateToLocale(recommendation.lastUpdateDate) }}</div>
                <div class="recommendation__content" v-html="textContent"></div>
            </VJoyWrapper>
        </VJoyWrapper>
        <div v-if="editMode && !isSample" class="action-edit toolbar">
            <VJoyDialogTrigger :dialog="removeDialogRef" @click="emitRemoval(recommendation.id)">
                <VJoyIconButton
                    :label="$t('general.delete')"
                    class="recommendation__remove-button"
                    color="red"
                    icon="trash"
                    size="small"
                    variant="ghost"
                />
            </VJoyDialogTrigger>
        </div>
    </VJoyWrapper>
</template>
<script lang="ts" setup>
    import {toRef} from 'vue';
    import {computed, useFormat} from '#imports';
    import {nl2br} from '@malt/common';
    import {sanitize} from '@malt/sanitize';
    import type {VJoyDialog} from '@maltjoy/core-vue';
    import {VJoyAvatar, VJoyIconButton, VJoyDialogTrigger, VJoyIcon, VJoyLink, VJoyWrapper} from '@maltjoy/core-vue';
    import type {Recommendation} from '~/types/domain/Recommendations';

    type Props = {
        modelValue: Recommendation;
        isEditMode: boolean;
        isSample: boolean;
        removeDialogRef?: InstanceType<typeof VJoyDialog> | null;
    };

    const props = defineProps<Props>();
    type Emits = {
        (e: 'remove', id: string): void;
    };
    const emit = defineEmits<Emits>();

    // Composables
    const {formatDateToShort} = useFormat();

    const recommendation = toRef(props, 'modelValue');
    const editMode = toRef(props, 'isEditMode');

    const textContent = computed(() => nl2br(sanitize(recommendation.value.textContent)));

    const authorProfileUrl = computed(() => {
        if (!recommendation.value.authorProfileUrl) return undefined;
        const url = new URL(recommendation.value.authorProfileUrl, 'http://dummy');
        url.searchParams.set('fromRecommendationList', 'true');
        return url.pathname + url.search;
    });

    function dateToLocale(date: Date) {
        return formatDateToShort(date);
    }

    function emitRemoval(id: string) {
        emit('remove', id);
    }
</script>
<style lang="scss" scoped>
    @use '../../../../node_modules/@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .recommendation {
        &--sample {
            opacity: 0.3;
        }

        &__header-avatar {
            //use same size as icon
            --avatar-size: 46px;

            @include mq.screen_xxs(max) {
                display: none;
            }
        }

        &__link-name {
            :deep(.joy-link) {
                border: 0;
            }
        }

        &__author-company,
        &__author-job {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            overflow: hidden;
            word-break: normal;
            overflow-wrap: anywhere;
            text-align: left;
        }

        &__date {
            font-size: var(--joy-font-size-primary-300);
        }

        &__content {
            margin-top: var(--joy-core-spacing-2);
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
            word-break: normal;
            overflow-wrap: anywhere;
            text-align: left;
        }

        &__remove-button {
            :deep(.joy-icon),
            &:hover :deep(.joy-icon) {
                color: var(--joy-color-error-50) !important; // I know
            }
        }
    }

    .action-edit {
        margin-left: auto;
    }
</style>
